import { useGeneral } from '@/store/general';
import { useUserConfig } from '@/store/user-config';
import { adminRoutes } from './admin-routes';
import { internalRoutes } from './internal-routes';
import type { RouteRecordRaw } from 'vue-router';
import type { Pinia } from 'pinia';

const Internal = () => import('@pages/internal/index.vue');
const Admin = () => import('@pages/admin/index.vue');

const Account = () => import('@pages/account/index.vue');
const Login = () => import('@pages/login/index.vue');
const ResetPassword = () => import('@pages/reset-password/index.vue');
const ComponentsPage = () => import('@pages/components-page/index.vue');
const Orders = () => import('@pages/orders/index.vue');
const Suppliers = () => import('@pages/suppliers/index.vue');
const Colors = () => import('@pages/colors/index.vue');
const Sampling = () => import('@pages/samples/index.vue');
const Products = () => import('@pages/products/index.vue');
const Product = () => import('@pages/product/index.vue');
const ArticlesTab = () => import('@pages/product/tabs/articles/index.vue');
const BomTab = () => import('@pages/product/tabs/bom/index.vue');
const FilesTab = () => import('@pages/product/tabs/files/index.vue');
const SamplingTab = () => import('@pages/product/tabs/sampling/index.vue');
const MchartTab = () => import('@pages/product/tabs/mchart/index.vue');
const TimelineTab = () => import('@pages/product/tabs/timeline/index.vue');
const PricingTab = () => import('@pages/product/tabs/pricing/index.vue');
const SupplierSampleRound = () => import('@pages/supplier-sample-round/index.vue');
const NotFound = () => import('@pages/not-found/index.vue');
const ShopifyClaim = () => import('@pages/shopify-claim/index.vue');
const MeasurementCharts = () => import('@pages/measurement-lists/index.vue');
const MeasurementChartDetail = () => import('../pages/measurement-lists/measurement-list-detail-page.vue');

// The main app's lazy route chunks, for idle preloading after boot (see lib/preload-routes.ts).
// Admin/internal child routes are deliberately left out — they're rarely visited.
export const routeComponentImports = [
  Products,
  Product,
  ArticlesTab,
  BomTab,
  FilesTab,
  SamplingTab,
  MchartTab,
  TimelineTab,
  PricingTab,
  MeasurementCharts,
  ComponentsPage,
  Orders,
  Suppliers,
  Colors,
  Sampling,
  SupplierSampleRound,
  Account,
  Admin,
  Internal,
  Login,
  ResetPassword,
  NotFound,
];

export const routes: (pinia: Pinia) => RouteRecordRaw[] = (pinia: Pinia) => [
  // Internal Pages
  { name: 'internal', path: '/plm/internal', component: Internal, children: internalRoutes },

  // Admin pages
  { name: 'admin', path: '/plm/admin', component: Admin, children: adminRoutes },

  {
    name: 'login',
    path: '/plm/login',
    component: Login,
    meta: { noAuth: true },
  },

  {
    name: 'reset-password',
    path: '/plm/resetPassword',
    component: ResetPassword,
    meta: { noAuth: true },
  },

  {
    name: 'account',
    path: '/plm/account',
    component: Account,
    beforeEnter: () => {
      useGeneral().headTitle = 'Account | Resourced';
    },
  },

  {
    path: '/plm',
    redirect: { name: 'products', query: { view: useUserConfig(pinia).settings?.products?.latestView } },
  },
  {
    name: 'products',
    path: '/plm/products/:articleId?',
    component: Products,
    meta: { overlayParam: 'articleId' },
    beforeEnter: () => {
      useGeneral().headTitle = 'Products | Resourced';
    },
  },
  {
    path: '/plm/product/:id',
    component: Product,
    children: [
      {
        name: 'product',
        path: '',
        redirect: () => ({ name: useUserConfig(pinia).settings?.product?.defaultTab ?? 'product-articles' }),
      },
      {
        name: 'product-articles',
        path: 'articles/:articleId?',
        component: ArticlesTab,
        meta: { overlayParam: 'articleId' },
      },
      { name: 'product-bom', path: 'bom', component: BomTab },
      {
        name: 'product-pricing',
        path: 'pricing/:articleId?',
        component: PricingTab,
        meta: { overlayParam: 'articleId' },
      },
      { name: 'product-files', path: 'files/:fileId?', component: FilesTab, meta: { overlayParam: 'fileId' } },
      {
        name: 'product-sampling',
        path: 'sampling/:roundNumber?',
        component: SamplingTab,
        meta: { overlayParam: 'roundNumber' },
      },
      { name: 'product-mchart', path: 'mchart/:mchartId?', component: MchartTab, meta: { overlayParam: 'mchartId' } },
      { name: 'product-timeline', path: 'timeline', component: TimelineTab },
    ],
  },
  {
    name: 'components',
    path: '/plm/components',
    component: ComponentsPage,
    beforeEnter: () => {
      useGeneral().headTitle = 'Components | Resourced';
    },
  },
  {
    name: 'orders',
    path: '/plm/orders/:orderNumber?',
    component: Orders,
    meta: { overlayParam: 'orderNumber' },
    beforeEnter: () => {
      useGeneral().headTitle = 'Orders | Resourced';
    },
  },
  {
    name: 'suppliers',
    path: '/plm/suppliers',
    component: Suppliers,
    beforeEnter: () => {
      useGeneral().headTitle = 'Suppliers | Resourced';
    },
  },
  {
    name: 'colors',
    path: '/plm/colors',
    component: Colors,
    beforeEnter: () => {
      useGeneral().headTitle = 'Colors | Resourced';
    },
  },
  {
    name: 'samples',
    path: '/plm/samples/:roundNumber?',
    component: Sampling,
    meta: { overlayParam: 'roundNumber' },
    beforeEnter: () => {
      useGeneral().headTitle = 'Samples | Resourced';
    },
  },
  {
    // The optional `:id?` param drives the detail fly-in panel on the overview,
    // so an open fly-in is linkable and survives a reload (mirrors the products
    // page's `:articleId?`). Filters live in the query string.
    name: 'measurement-charts',
    path: '/plm/measurement-charts/:id?',
    component: MeasurementCharts,
    meta: { overlayParam: 'id' },
    beforeEnter: () => {
      useGeneral().headTitle = 'Measurement lists | Resourced';
    },
  },
  {
    name: 'measurement-chart',
    path: '/plm/measurement-chart/:id',
    component: MeasurementChartDetail,
    beforeEnter: () => {
      useGeneral().headTitle = 'Measurement List | Resourced';
    },
  },
  {
    name: 'supplier-sample-round',
    path: '/plm/supplier/sample-round/:token',
    component: SupplierSampleRound,
    meta: { noAuth: true },
  },
  {
    // Landing page after Shopify App Store install. The page handles its own auth bounce
    // (unauthenticated visitors get redirected to login with ?redirect= preserving the token).
    name: 'shopify-claim',
    path: '/plm/shopify/claim',
    component: ShopifyClaim,
    meta: { noAuth: true },
  },
  {
    path: '/',
    redirect: { name: 'products', query: { view: useUserConfig(pinia).settings?.products?.latestView } },
  },

  // Catch-all 404. Must stay last. Replaces the server-side 404 detection that
  // the SSR runner used to do via router matched.length.
  {
    name: 'not-found',
    path: '/:pathMatch(.*)*',
    component: NotFound,
    meta: { noAuth: true },
  },
];
